import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MagnifyingGlass, MapPin, Crosshair } from "@phosphor-icons/react";

// Patch broken default icon paths (Leaflet CRA bug)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

/**
 * Interactive map for picking a geofence center + radius.
 * Props:
 *   - lat / lng / radius (controlled values, may be null)
 *   - onChange({ lat, lng, radius })
 *   - addressHint (string to pre-fill the search box)
 */
const MapPicker = ({ lat, lng, radius = 100, onChange, addressHint = "" }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const [search, setSearch] = useState(addressHint);
  const [searching, setSearching] = useState(false);
  const [err, setErr] = useState(null);

  // Initialize map once
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const startLat = typeof lat === "number" ? lat : 39.5;
    const startLng = typeof lng === "number" ? lng : -98.35;
    const startZoom = typeof lat === "number" ? 16 : 4;

    const map = L.map(containerRef.current, {
      zoomControl: true,
      scrollWheelZoom: true,
    }).setView([startLat, startLng], startZoom);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap",
      maxZoom: 19,
    }).addTo(map);

    const marker = L.marker([startLat, startLng], { draggable: true }).addTo(map);
    const circle = L.circle([startLat, startLng], {
      radius: radius,
      color: "#007AFF",
      fillColor: "#007AFF",
      fillOpacity: 0.15,
      weight: 2,
    }).addTo(map);

    marker.on("drag", (e) => {
      const { lat: nlat, lng: nlng } = e.target.getLatLng();
      circle.setLatLng([nlat, nlng]);
    });
    marker.on("dragend", (e) => {
      const { lat: nlat, lng: nlng } = e.target.getLatLng();
      onChange?.({ lat: nlat, lng: nlng, radius });
    });

    map.on("click", (e) => {
      marker.setLatLng(e.latlng);
      circle.setLatLng(e.latlng);
      onChange?.({ lat: e.latlng.lat, lng: e.latlng.lng, radius });
    });

    mapRef.current = map;
    markerRef.current = marker;
    circleRef.current = circle;

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      circleRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to external lat/lng changes
  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return;
    if (typeof lat !== "number" || typeof lng !== "number") return;
    markerRef.current.setLatLng([lat, lng]);
    circleRef.current.setLatLng([lat, lng]);
    if (mapRef.current.getZoom() < 13) mapRef.current.setView([lat, lng], 16);
    else mapRef.current.panTo([lat, lng]);
  }, [lat, lng]);

  // React to radius changes
  useEffect(() => {
    if (!circleRef.current) return;
    circleRef.current.setRadius(radius);
  }, [radius]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    const q = search.trim();
    if (!q) return;
    setSearching(true);
    setErr(null);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      const data = await res.json();
      if (!data || data.length === 0) { setErr("Address not found"); return; }
      const nlat = parseFloat(data[0].lat);
      const nlng = parseFloat(data[0].lon);
      onChange?.({ lat: nlat, lng: nlng, radius });
    } catch {
      setErr("Lookup failed");
    } finally {
      setSearching(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { setErr("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => onChange?.({ lat: pos.coords.latitude, lng: pos.coords.longitude, radius }),
      () => setErr("Could not get your location"),
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="map-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSearch(); } }}
            placeholder="Search address or landmark"
            className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none pl-8 pr-3 py-2 text-sm"
          />
        </div>
        <button
          data-testid="map-search-btn"
          type="button"
          onClick={handleSearch}
          disabled={searching}
          className="px-3 py-2 border border-white/15 hover:bg-white/5 text-xs uppercase tracking-wider font-semibold disabled:opacity-50"
        >
          <MapPin size={14} className="inline -mt-0.5 mr-1"/>{searching ? "..." : "Find"}
        </button>
        <button
          data-testid="map-mylocation-btn"
          type="button"
          onClick={useMyLocation}
          title="Use my current location"
          className="px-3 py-2 border border-white/15 hover:bg-white/5"
        >
          <Crosshair size={14} />
        </button>
      </div>
      {err && <div className="text-xs text-red-400">{err}</div>}
      <div ref={containerRef} data-testid="map-container" className="w-full h-72 rounded border border-white/10 overflow-hidden" />
      <div className="flex items-center justify-between text-xs text-zinc-500 font-mono">
        <span>
          {typeof lat === "number" && typeof lng === "number"
            ? `${lat.toFixed(5)}, ${lng.toFixed(5)}`
            : "Click map or search to drop a pin"}
        </span>
        <span>Radius: {radius}m</span>
      </div>
    </div>
  );
};

export default MapPicker;
